// Groupes et Pages — deux écrans, un seul composant.
//
// C'est ici que la migration cesse d'être un déménagement pour devenir un gain.
// Ces deux écrans étaient DEUX fichiers de 100 lignes qui ne différaient que
// par des mots : « Mes groupes » / « Mes pages », `subscribeMiens` /
// `subscribeMiennes`, « Aucun autre groupe » / « Aucune autre page ». Toute
// correction devait être faite deux fois, et l'oubli ne se voyait pas — c'est
// exactement ainsi que deux écrans jumeaux finissent par ne plus se ressembler.
//
// Ici la différence tient dans deux objets de configuration, et le reste — la
// recherche, les abonnements, les suggestions, les états vides — n'est écrit
// qu'une fois.

import { useEffect, useMemo, useState } from "react";
import Store from "../../store.js";
import { suggererEspaces, expliquer } from "../../algorithmes.js";
import { creerGroupe, creerPage } from "../../views/espaces-creer.js";
import CarteEspace from "../CarteEspace.jsx";
import Icone from "../Icone.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { useAbonnement, useSignaux } from "../hooks.js";

const GROUPES = {
  genre: "groupe",
  titre: "Groupes",
  placeholder: "Rechercher un groupe…",
  titreMiens: "Mes groupes",
  titreTous: "Tous les groupes",
  aucunAutre: "Aucun autre groupe pour l'instant.",
  vide: {
    symbole: "👥",
    titre: "Vous n'êtes dans aucun groupe",
    texte: "Rejoignez-en un ci-dessous, ou créez le vôtre.",
  },
  abonnerMiens: (cb) => Store.groupes.subscribeMiens(cb),
  abonnerTous: (cb) => Store.groupes.subscribe(cb),
  creer: creerGroupe,
};

const PAGES = {
  genre: "page",
  titre: "Pages",
  placeholder: "Rechercher une page…",
  titreMiens: "Mes pages",
  titreTous: "Toutes les pages",
  aucunAutre: "Aucune autre page pour l'instant.",
  vide: {
    symbole: "📄",
    titre: "Aucune page",
    texte: "Abonnez-vous à une page ci-dessous, ou créez la vôtre.",
  },
  abonnerMiens: (cb) => Store.pages.subscribeMiennes(cb),
  abonnerTous: (cb) => Store.pages.subscribe(cb),
  creer: creerPage,
  // Une page peut mettre en avant du contenu ; un groupe non. La règle du jeu
  // doit donc être à portée de main, sur cet écran-là seulement.
  politique: true,
};

/** Une liste de vignettes, ou rien. */
function Liste({ espaces, genre, description = true }) {
  if (!espaces.length) return null;
  return (
    <div className="liste-groupes">
      {espaces.map((x) => (
        <CarteEspace espace={x} genre={genre} description={description} key={x.id} />
      ))}
    </div>
  );
}

/**
 * « Suggestions pour vous » : d'abord les espaces où sont déjà mes amis, ensuite
 * ceux qui nomment mon église ou ma ville. Le calcul est dans algorithmes.js ;
 * ici on affiche, et on dit pourquoi.
 */
function SuggestionsEspaces({ resultats, genre }) {
  if (!resultats.length) return null;
  return (
    <>
      <h2 className="titre-section">Suggestions pour vous</h2>
      <div className="liste-groupes">
        {resultats.map(({ espace, raisons }) => (
          <div className="espace-suggere" key={espace.id}>
            <CarteEspace espace={espace} genre={genre} description={false} />
            <span className="petit texte-doux espace-suggere__raison">
              {expliquer(raisons, 1)[0] || ""}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function ListeEspaces({ ctx, conf }) {
  const [requete, setRequete] = useState("");
  const miens = useAbonnement(conf.abonnerMiens, null, [conf]);
  const tous = useAbonnement(conf.abonnerTous, null, [conf]);
  const versionSignaux = useSignaux(ctx.signaux);

  // Qui est dans quel espace ne tient pas dans un abonnement : on va le chercher
  // une fois la liste connue. Les suggestions s'affinent alors toutes seules.
  const [membresDe, setMembresDe] = useState(new Map());
  const idsTous = (tous || []).map((x) => x.id).join(",");
  useEffect(() => {
    if (!idsTous || !Store.signaux?.membresDesEspaces) return;
    let vivant = true;
    Store.signaux
      .membresDesEspaces(conf.genre, idsTous.split(","))
      // Sans la composition, on suggère quand même sur l'église et la ville :
      // une suggestion moins fine vaut mieux qu'un écran qui n'affiche rien.
      .catch(() => new Map())
      .then((carte) => vivant && setMembresDe(carte));
    return () => {
      vivant = false;
    };
  }, [idsTous, conf.genre]);

  const filtrer = (liste) => {
    const q = requete.trim().toLowerCase();
    if (!q) return liste;
    return liste.filter(
      (x) => x.nom.toLowerCase().includes(q) || (x.description || "").toLowerCase().includes(q)
    );
  };

  const listeMiens = miens || [];
  const listeTous = tous || [];
  const idsMiens = new Set(listeMiens.map((x) => x.id));
  const autres = filtrer(listeTous.filter((x) => !idsMiens.has(x.id)));

  const suggestions = useMemo(() => {
    if (!ctx.signaux || !listeTous.length) return [];
    return suggererEspaces(listeTous, ctx.signaux.instantane(), {
      dejaDedans: idsMiens,
      membresDe,
      combien: 4,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tous, miens, membresDe, versionSignaux]);

  return (
    <div className="colonne">
      <div className="entete-page">
        <h1 className="titre-page">{conf.titre}</h1>
        <button className="btn btn--primaire btn--petit" onClick={() => conf.creer()}>
          <Icone nom="plus" taille={16} /> Créer
        </button>
      </div>

      <input
        className="champ"
        type="search"
        placeholder={conf.placeholder}
        autoComplete="off"
        value={requete}
        onChange={(e) => setRequete(e.target.value)}
      />

      <h2 className="titre-section">{conf.titreMiens}</h2>
      {miens === null ? (
        <Chargement />
      ) : filtrer(listeMiens).length ? (
        <Liste espaces={filtrer(listeMiens)} genre={conf.genre} />
      ) : (
        <Vide {...conf.vide} />
      )}

      <SuggestionsEspaces resultats={suggestions} genre={conf.genre} />

      <h2 className="titre-section">{conf.titreTous}</h2>
      {autres.length ? (
        <Liste espaces={autres} genre={conf.genre} />
      ) : (
        <p className="texte-doux petit" style={{ padding: "0 4px" }}>
          {conf.aucunAutre}
        </p>
      )}

      {conf.politique && (
        <a className="carte lien-politique" href="#/sponsoring">
          <Icone nom="options" taille={20} />
          <span>
            <strong>Politique de sponsoring</strong>
            <span className="petit texte-doux">
              Ce qu'une page peut mettre en avant, et ce qui est refusé.
            </span>
          </span>
        </a>
      )}
    </div>
  );
}

export function Groupes({ ctx }) {
  return <ListeEspaces ctx={ctx} conf={GROUPES} />;
}

export function Pages({ ctx }) {
  return <ListeEspaces ctx={ctx} conf={PAGES} />;
}
